import { useEffect, useState } from 'react'
import { IGuarantorOption } from 'components/modals/guarantorPicker'
import { ITerms } from './steps/StepTerms'

export const EMPTY_TERMS: ITerms = {
    installments: '',
    feeType: 'percentage',
    fee: '',
    feeMode: 'lip_ma',
    startDate: '',
}

interface IStoredDraft {
    borrower?: ILoanCandidate
    amount: string
    terms: ITerms
    guarantors: IGuarantorOption[]
    note: string
}

const key = (moneypoolId?: string) => `hamyan:grant-draft:${moneypoolId ?? ''}`

/**
 * The wizard's answers, kept across an unmount.
 *
 * The payout step can send the manager off to `/manage-bank-cards/add` — the
 * borrower may have no registered account. That unmounts the wizard, and on
 * the way back the "no borrower picked" guard bounced them to step 1 with all
 * four answered questions gone. Reported from real use: «when clicking back
 * from the create-bank-account page, we return to step 1 :(».
 *
 * **sessionStorage, and restored in a `useState` initialiser, not an effect.**
 * The initialiser matters: the step lives in the URL, so on the first render
 * back the wizard is already at `?step=5`, and a draft that only lands on the
 * second render lets the guard fire first and redirect anyway.
 *
 * sessionStorage rather than localStorage because a half-built loan is
 * session-scoped by nature — closing the tab should not leave a stale draft
 * waiting to be mistaken for a real one tomorrow.
 */
export default function useGrantDraft(moneypoolId?: string) {
    const stored = (): Partial<IStoredDraft> => {
        try {
            const raw = sessionStorage.getItem(key(moneypoolId))
            return raw ? JSON.parse(raw) : {}
        } catch {
            // a corrupt or unavailable store must not take the page down;
            // the worst case is an empty wizard, which is where it started
            return {}
        }
    }

    const initial = stored()
    const [borrower, setBorrower] = useState<ILoanCandidate | undefined>(
        initial.borrower
    )
    const [amount, setAmount] = useState(initial.amount ?? '')
    const [terms, setTerms] = useState<ITerms>(initial.terms ?? EMPTY_TERMS)
    const [guarantors, setGuarantors] = useState<IGuarantorOption[]>(
        initial.guarantors ?? []
    )
    const [note, setNote] = useState(initial.note ?? '')

    useEffect(() => {
        try {
            sessionStorage.setItem(
                key(moneypoolId),
                JSON.stringify({ borrower, amount, terms, guarantors, note })
            )
        } catch {
            // storage full or blocked — the wizard still works in-memory
        }
    }, [moneypoolId, borrower, amount, terms, guarantors, note])

    const clear = () => {
        try {
            sessionStorage.removeItem(key(moneypoolId))
        } catch {
            /* nothing to clean up */
        }
    }

    return {
        borrower,
        setBorrower,
        amount,
        setAmount,
        terms,
        setTerms,
        guarantors,
        setGuarantors,
        note,
        setNote,
        clear,
    }
}
